<template>
	<div :id="id" ref="showMoreTable" class="show-more-table">
		<div ref="componentWrapper" class="component-wrapper">
			<div
				ref="componentContainer"
				:class="['scroll-fade', { '--show-gradient': tableWillScroll }]"
			>
				<!-- @slot Add the TableFixedLeft component (optional) and the table template here -->
				<slot name="table" :computedTableRows="computedTableRows" />
			</div>
		</div>

		<div
			class="controls"
			:class="[
				isSmall ? 'small' : 'box',
				{
					expanded: isExpanded,
					'pagination-enabled': displayPaginationComponent
				}
			]"
		>
			<!-- dom structure for small layout -->
			<template v-if="isSmall">
				<div class="pagination-wrapper">
					<Pagination
						v-if="displayPaginationComponent"
						:pageSize="paginationRows"
						:data="tableRowData"
						@pagination:change="updateData"
					/>
				</div>
				<button
					v-if="displayToggleButton"
					ref="toggleButton"
					class="button--simple"
					@click="onToggleButtonClick"
				>
					<span>{{ buttonLabel }}</span
					><span
						class="icon"
						:class="isExpanded ? 'icon-up' : 'icon-down'"
						aria-hidden="true"
					/>
				</button>
			</template>

			<!-- dom structure for medium+ layout -->
			<template v-else>
				<div class="button-container col md-4">
					<button
						v-if="displayToggleButton"
						ref="toggleButton"
						class="button--simple"
						@click="onToggleButtonClick"
					>
						<span>{{ buttonLabel }}</span
						><span
							class="icon"
							:class="isExpanded ? 'icon-up' : 'icon-down'"
							aria-hidden="true"
						/>
					</button>
				</div>
				<div class="pagination-wrapper col md-12">
					<Pagination
						v-if="displayPaginationComponent"
						:pageSize="paginationRows"
						:data="tableRowData"
						@pagination:change="updateData"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import { debounce } from "lodash";
import { EventBus } from "@vg-faset/core";
import { UniqueId } from "../../../utilities";
import Pagination from "@vg-faset/ui/src/components/pagination/Pagination";

/**
 * A table wrapper component that will show a number of rows and includes a 'show more' button that will do an expand animation to add more rows to the table. Pagination is an option for larger datasets.
 */
export default {
	name: "ShowMoreTable",

	mql: null,

	components: {
		Pagination
	},

	props: {
		/**
		 * Add a unique ID to the table. If none is provided, the component generates a unique ID.
		 */
		id: {
			type: String,
			default: () => new UniqueId("show-more-table-").generate()
		},

		/**
		 * An array of objects where each object contains the table row data.
		 */
		tableRowData: {
			type: Array,
			required: true,
			default: () => []
		},

		/**
		 * The number of rows to show in initial collapsed state.
		 */
		startingRows: {
			type: Number,
			required: false,
			default: 10
		},

		/**
		 * Set to `true` to use pagination.
		 */
		pagination: {
			type: Boolean,
			required: false,
			default: true
		},

		/**
		 * If pagination is enabled, set the number of rows per page.
		 */
		paginationRows: {
			type: Number,
			required: false,
			default: 25
		},

		/**
		 * The button label for the **show more** action.
		 */
		buttonLabelExpand: {
			type: String,
			required: false,
			default: "Show all"
		},

		/**
		 * The button label for the **show less** action.
		 */
		buttonLabelCollapse: {
			type: String,
			required: false,
			default: "Show less"
		},

		/**
		 * Set the offset to scroll past the table top element when collapsing. Defaults to 108 to account for inPageNav.
		 */
		scrollTopOffset: {
			type: Number,
			required: false,
			default: 108
		}
	},

	data() {
		return {
			maxHeightBuffer: 16,
			filteredData: this.tableRowData,
			isExpanded: false,
			isSmall: false,
			hidePagination: true,
			collapsedHeight: undefined,
			tableWillScroll: false
		};
	},

	computed: {
		computedTableRows() {
			return this.isExpanded ? this.filteredData : this.getCondensedTableRows();
		},

		displayToggleButton() {
			return this.tableRowData.length > this.startingRows;
		},

		displayPaginationComponent() {
			return (
				this.tableRowData.length > this.paginationRows &&
				this.pagination &&
				this.isExpanded &&
				!this.hidePagination
			);
		},

		buttonLabel() {
			return this.isExpanded
				? this.buttonLabelCollapse
				: this.buttonLabelExpand;
		}
	},

	methods: {
		updateData(pageRows) {
			this.filteredData = pageRows;
		},

		getCondensedTableRows() {
			return this.tableRowData.slice(0, this.startingRows);
		},

		getComponentHeight() {
			return this.$refs.componentContainer.clientHeight + this.maxHeightBuffer;
		},

		setMaxHeight(value) {
			if (value > this.maxHeightBuffer) {
				this.$refs.componentWrapper.style.maxHeight = `${value}px`;
			}
		},

		onToggleButtonClick() {
			this.$refs.toggleButton.blur();

			// collapse the table
			if (this.isExpanded) {
				this.hidePagination = true;
				this.onScrollToElementTop();

				// The table is isExpanded, this will close it. We want to change the wrapper's
				// max-height to scroll the table back to its original height over 0.5s
				this.setMaxHeight(this.collapsedHeight);

				// This updates the table to the original number of rows in its consolidated state
				// after the table has finished scrolling closed.
				setTimeout(() => (this.isExpanded = false), 500);
			}

			// expand the table
			else {
				this.hidePagination = false;

				// The table is condensed, this will expand it. If there is no collapsedHeight value,
				// set it before expanding the table.
				if (!this.collapsedHeight) {
					this.collapsedHeight = this.getComponentHeight();
					this.setMaxHeight(this.collapsedHeight);
				}

				// changes data to use all table data
				this.isExpanded = true;

				// changes the div max-height to a new value which triggers the css transition
				this.$nextTick(function () {
					const height = this.getComponentHeight();
					this.setMaxHeight(height);
				});
			}
		},

		onScrollToElementTop() {
			const tableTop =
				this.$refs.showMoreTable.getBoundingClientRect().top -
				this.scrollTopOffset;

			const scroll = tableTop - tableTop / 1.4;

			if (scroll < 0) {
				window.requestAnimationFrame(this.onScrollToElementTop);
				window.scrollBy(0, scroll);
			}
		},

		matchMedia() {
			this.mql = window.matchMedia("(max-width: 767px)");
			this.isSmall = this.mql.matches;
		},

		onQueryMedia(e) {
			this.isSmall = e.matches;
		},

		onPageResize: debounce(function () {
			this.$nextTick(() => {
				this.setMaxHeight(this.getComponentHeight());
			});
		}, 750),

		onTableWillScroll(willScroll, tableId) {
			const hasTableFixedLeft =
				this.$refs.componentContainer &&
				this.$refs.componentContainer.querySelector(`#${tableId}`) !== null;
			this.tableWillScroll = willScroll && hasTableFixedLeft;
		}
	},

	watch: {
		tableRowData: function (data) {
			// If new data gets fed to the component, this updates
			// filteredData and table max height values
			this.filteredData = data;
			this.$nextTick(function () {
				this.setMaxHeight(this.getComponentHeight());
			});
		}
	},

	created() {
		this.matchMedia();
		this.mql.addListener(this.onQueryMedia);
		window.addEventListener("resize", this.onPageResize);
	},

	mounted() {
		EventBus.$on("on-table-will-scroll", this.onTableWillScroll);
	},

	beforeDestroy() {
		this.mql.removeListener(this.onQueryMedia);
		window.removeEventListener("resize", this.onPageResize);
		EventBus.$off("on-table-will-scroll", this.onTableWillScroll);
	}
};
</script>

<style lang="scss" scoped>
.component-wrapper {
	height: auto;
	overflow: hidden;
	transition: max-height 0.5s ease-in;
}

.controls {
	.pagination-wrapper {
		opacity: 0;
		transition: opacity 0.3s 0.45s ease-in;
	}

	&.expanded {
		.pagination-wrapper {
			opacity: 1;
		}
	}

	&.small {
		.pagination-wrapper {
			opacity: 0;
			height: 0;
			transition: all 0.1s ease-in;
		}

		&.expanded.pagination-enabled {
			margin-top: 16px;

			.pagination-wrapper {
				opacity: 1;
				height: 42px;
				transition: height 0.3s 0.3s ease-in, opacity 0.3s 0.6s ease-in;
			}
		}
	}
}

.button-container {
	display: flex;
	margin-top: 16px;

	.button-simple {
		align-items: center;
	}
}

.button--simple {
	color: $color--red-3;
	margin-top: 16px;

	@include from-md {
		margin-top: 0;
		height: 42px;
	}

	.small.expanded.pagination-enabled & {
		margin-top: 24px;

		@include from-md {
			height: 42px;
			margin-top: 0;
		}
	}
}

// override pagination component margin-top
.pagination {
	margin-top: 0;

	&::v-deep .secondary-select {
		margin-top: 16px;
	}
}
</style>
